import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/ui/inputs/Input';
import { Section } from '@/shared/ui/layout/Section';
import { usePinflLimit } from '@/entities/pinfl';
import { formatUsd } from '@/shared/utils/currency';
import { env } from '@/shared/lib/env';
import { cn } from '@/shared/utils/cn';
import { extractErrorMessage } from '@/shared/api/errors';
import type { AdminParcelFormValues } from '@/features/parcel-form/modules/types';

type Props = { form: UseFormReturn<AdminParcelFormValues> };

export const FeaturesParcelFormSectionPersonal = ({ form }: Props) => {
  const { t } = useTranslation();
  const { register, control, formState, setValue } = form;
  const errors = formState.errors;
  const pinfl = useWatch({ control, name: 'pinfl' }) ?? '';
  const limit = usePinflLimit(pinfl);
  const translateError = (msg?: string) =>
    msg ? t(msg, { length: env.pinflLength }) : undefined;
  // Passport seriya is exactly 9 chars: 2 latin upper + 7 digits. Mirror
  // the trek_id approach so the value is upper-cased both in the DOM and
  // in RHF state regardless of how the operator typed it.
  const passportReg = register('passport_series', {
    setValueAs: (v: unknown) => (typeof v === 'string' ? v.toUpperCase() : ''),
  });

  return (
    <Section title={t('parcels.form.sections.personal')}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Input
          label={t('parcels.form.fields.lastName')}
          required
          error={translateError(errors.last_name?.message)}
          {...register('last_name')}
        />
        <Input
          label={t('parcels.form.fields.firstName')}
          required
          error={translateError(errors.first_name?.message)}
          {...register('first_name')}
        />
        <Input
          label={t('parcels.form.fields.middleName')}
          required
          error={translateError(errors.middle_name?.message)}
          {...register('middle_name')}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          label={t('parcels.form.fields.phone')}
          required
          inputMode="tel"
          error={translateError(errors.phone_number?.message)}
          {...register('phone_number')}
        />
        <Input
          label={t('parcels.form.fields.passport')}
          required
          autoCapitalize="characters"
          maxLength={9}
          error={translateError(errors.passport_series?.message)}
          {...passportReg}
          onChange={(e) => {
            const upper = e.target.value.toUpperCase();
            if (e.target.value !== upper) e.target.value = upper;
            setValue('passport_series', upper, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        />
      </div>
      <Input
        label={t('parcels.form.fields.pinfl')}
        required
        inputMode="numeric"
        maxLength={env.pinflLength}
        error={translateError(errors.pinfl?.message)}
        hint={
          !limit.isFetched && !limit.isFetching ? null : limit.isFetching ? (
            <span className="text-ink-muted">{t('parcels.form.pinfl.checking')}</span>
          ) : limit.error ? (
            <span className="text-red-600">{extractErrorMessage(limit.error)}</span>
          ) : limit.data ? (
            <span
              className={cn(
                'font-medium',
                limit.data.used_amount > limit.data.total_limit
                  ? 'text-red-600'
                  : 'text-emerald-600',
              )}
            >
              {t('parcels.form.pinfl.usage', {
                used: formatUsd(limit.data.used_amount),
                total: formatUsd(limit.data.total_limit),
              })}
            </span>
          ) : null
        }
        {...register('pinfl')}
      />
    </Section>
  );
};
